/* eslint-disable @typescript-eslint/no-empty-function */
import { Dispatch, SetStateAction, useMemo, useState } from 'react';
import arrow from '../../../../assets/images/icons/arrow.svg';
import coinsSidebar from '../../../../assets/images/icons/coinsSidebar.svg';
import companySidebar from '../../../../assets/images/icons/companySidebar.svg';
import edit from '../../../../assets/images/icons/edit.svg';
import home from '../../../../assets/images/icons/home.svg';
import route from '../../../../assets/images/icons/route.svg';
import user from '../../../../assets/images/icons/user.svg';
import useGetGeneralAppData from '../../../../hooks/useGetGeneralAppData';
import floatToCurrency from '../../../../utils/floatToCurrency';
import formatPhone from '../../../../utils/formatPhone';
import hideCpf from '../../../../utils/hideCpf';
import hideEmail from '../../../../utils/hideEmail';
import hidePhone from '../../../../utils/hidePhone';
import FilterRadioButton from '../../../FilterRadioButtons';
import { FilterRadioButtonsContainer } from '../../../FilterRadioButtonsContainer';
import OpacityAnimation from '../../../OpacityAnimation';
import { DadosConsulta, EnderecosLt, IWorkplacesOptions, LinhasBia, LinhasExcecao, OpBia, OpExcecao, SelectedCardType } from '../../types';
import { Container, DataContainer, InnerDataContainer, SelectedViewContainer } from './styles';

interface IConsultDataAndSettingsCard {
  consultData: DadosConsulta;
  workplaces: EnderecosLt[];
  selectedWorkplaces: IWorkplacesOptions[];
  isMultiRoute: boolean;
  company: string;
  companyCode: string;
  criminalRisc: string;
  setEditConsultDataModalShow: Dispatch<SetStateAction<boolean>>;
  setEditSettingsDataModalShow: Dispatch<SetStateAction<boolean>>;
  routeName: string;
  routingType: number;
  linesBias: LinhasBia[];
  opsBias: OpBia[];
  linesException: LinhasExcecao[];
  opsException: OpExcecao[];
  isGettingMyPlace: boolean;
  hasCloserWorkplace: boolean;
  hasMyPlace: boolean;
  particularities: string;
}

export default function ConsultDataAndSettingsCard({
  consultData,
  workplaces,
  selectedWorkplaces,
  isMultiRoute,
  company,
  companyCode,
  criminalRisc,
  setEditConsultDataModalShow,
  setEditSettingsDataModalShow,
  routeName,
  routingType,
  linesBias,
  opsBias,
  linesException,
  opsException,
  isGettingMyPlace,
  hasCloserWorkplace,
  hasMyPlace,
  particularities,
}: IConsultDataAndSettingsCard) {
  const [selectedCard, setSelectedCard] = useState<SelectedCardType>('data');
  const [selectedWorkplace, setSelectedWorkplace] = useState<IWorkplacesOptions>(selectedWorkplaces[0]);

  const correspondingWorkplace = useMemo(() => (workplaces?.find((wp) => wp.codEndA === consultData.codEndA)), [consultData.codEndA, workplaces]);
  const workplaceIndex = useMemo(() => selectedWorkplaces?.indexOf(selectedWorkplace), [selectedWorkplaces, selectedWorkplace]);

  const criminalRiscColorMap: { [key: string]: string} = {
    'Sem indice': 'gray',
    'Baixo Risco': 'green',
    'Médio Risco': 'orange',
    'Alto Risco': 'red',
  };

  const { permissions, isCustomer } = useGetGeneralAppData();
  const routingPermissions = permissions.find((x) => x.codPag === 6);
  const canEdit = routingPermissions?.alterar;

  return (
    <Container>
      <DataContainer>
        <FilterRadioButtonsContainer>
          <FilterRadioButton
            selected={selectedCard === 'data'}
            onClick={() => setSelectedCard('data')}
          >
          Dados
          </FilterRadioButton>
          <FilterRadioButton
            selected={selectedCard === 'settings'}
            onClick={() => setSelectedCard('settings')}
          >
          Critérios
          </FilterRadioButton>
          {particularities && (
            <FilterRadioButton
              selected={selectedCard === 'particularities'}
              onClick={() => setSelectedCard('particularities')}
            >
              Particularidades
            </FilterRadioButton>
          )}
        </FilterRadioButtonsContainer>

        {selectedCard === 'data' && (
          <OpacityAnimation>
            <h3>{company} ({companyCode})</h3>
            {isMultiRoute && <h3>Rota: {routeName}</h3>}

            <SelectedViewContainer>
              <InnerDataContainer>
                <h4>
                  <img src={user} alt="" />
                  Dados funcionário
                </h4>
                <div>
                  <strong>CPF:</strong>
                  {' '}
                  {hideCpf(consultData.cpf) || 'Não informado'}
                </div>

                <div>
                  <strong>Matrícula:</strong>
                  {' '}
                  {consultData.matricula || 'Não informada'}
                </div>

                <div>
                  <strong>E-mail:</strong>
                  {' '}
                  {hideEmail(consultData.mail) || 'Não informado'}
                </div>

                <div>
                  <strong>Celular:</strong>
                  {' '}
                  {consultData.celular ? hidePhone(formatPhone(consultData.celular)) : 'Não informado'}
                </div>

                <div>
                  <strong>Nome:</strong>
                  {' '}
                  {consultData.nome}
                </div>
              </InnerDataContainer>

              <InnerDataContainer>
                <h4>
                  <img src={home} alt="" />
                  Endereço Funcionário
                </h4>

                <small className={criminalRiscColorMap[criminalRisc]}>
                  Criminalidade {criminalRisc}
                </small>

                <div>
                  <strong>CEP:</strong>
                  {' '}
                  {consultData.cep}
                </div>

                <div>
                  <strong>Endereço:</strong>
                  {' '}
                  {consultData.logradouro}, {consultData.num} <br /> {consultData.bairro} - {consultData.cidade} - {consultData.uf}
                </div>
              </InnerDataContainer>

              {isMultiRoute
                ?
                (
                  <InnerDataContainer>
                    <h4>
                      {workplaceIndex + 1 !== 1 && <img src={arrow} alt="back" className="primaryColor left" onClick={() => setSelectedWorkplace(selectedWorkplaces[workplaceIndex - 1])} />}
                      <img src={companySidebar} alt="" />
                      Local de trabalho #{workplaceIndex + 1}
                      {workplaceIndex + 1 !== selectedWorkplaces.length && <img src={arrow} alt="back" className="primaryColor right" onClick={() => setSelectedWorkplace(selectedWorkplaces[workplaceIndex + 1])} />}
                    </h4>

                    <strong>{selectedWorkplace?.denominacaoLT}</strong>

                    <div>
                      <strong>CEP:</strong>
                      {' '}
                      {selectedWorkplace?.cep}
                    </div>

                    <div>
                      <strong>Endereço:</strong>
                      {' '}
                      {selectedWorkplace?.logradouro}, {selectedWorkplace?.numero} <br /> {selectedWorkplace?.bairro} - {selectedWorkplace?.cidade} - {selectedWorkplace?.UF}
                    </div>
                  </InnerDataContainer>
                )
                :
                (
                  <InnerDataContainer>
                    <h4>
                      <img src={companySidebar} alt="" />
                  Local de trabalho
                    </h4>

                    <strong>{`${correspondingWorkplace?.denominacaoLT} ${correspondingWorkplace?.distanceFromEmployeeHome ? `(${(correspondingWorkplace?.distanceFromEmployeeHome / 1000).toFixed(2)}km)` : ''}`}</strong>

                    <div>
                      <strong>CEP:</strong>
                      {' '}
                      {correspondingWorkplace?.cep}
                    </div>

                    <div>
                      <strong>Endereço:</strong>
                      {' '}
                      {correspondingWorkplace?.logradouro}, {correspondingWorkplace?.numero} <br /> {correspondingWorkplace?.bairro} - {correspondingWorkplace?.cidade} - {correspondingWorkplace?.UF}
                    </div>

                    {hasMyPlace && (
                      <>
                        {isGettingMyPlace ? (
                          <small>
                        Verificando se existem locais mais próximos...
                          </small>
                        ) : hasCloserWorkplace ? (
                          <small className='orange'>
                        Existem locais mais próximos!
                          </small>
                        ) : (
                          <small className='green'>
                        Não existem locais mais próximos
                          </small>
                        )}
                      </>
                    )}
                  </InnerDataContainer>
                )}
            </SelectedViewContainer>

          </OpacityAnimation>
        )}

        {selectedCard === 'settings' && (
          <OpacityAnimation>
            <SelectedViewContainer>
              <InnerDataContainer>
                <h4>
                  <img src={route} alt="" />
                  Critérios de Rota
                </h4>
                <div>
                  <strong>Distância máxima caminhada:</strong>
                  {' '}
                  {consultData?.distancia} metros
                </div>

                <div>
                  <strong>Espelhamento:</strong>
                  {' '}
                  {consultData?.usaEspelhamento ? 'Utiliza' : 'Não utiliza'}
                </div>

                <div>
                  <strong>Fretamento:</strong>
                  {' '}
                  {consultData?.usaFretado ? 'Utiliza' : 'Não utiliza'}
                </div>

                {!isCustomer && (
                  <>
                    <div>
                      <strong>BIAS:</strong>
                      {' '}
                      {opsBias.length === 1 ? `${opsBias.length} operadora` : `${opsBias.length} operadoras`}
                      {' e '}
                      {linesBias.length === 1 ? `${linesBias.length} linha` : `${linesBias.length} linhas`}
                    </div>

                    <div>
                      <strong>Avoid:</strong>
                      {' '}
                      {opsException.length === 1 ? `${opsException.length} operadora` : `${opsException.length} operadoras`}
                      {' e '}
                      {linesException.length === 1 ? `${linesException.length} linha` : `${linesException.length} linhas`}
                    </div>
                  </>
                )}
              </InnerDataContainer>

              <InnerDataContainer>
                <h4>
                  <img src={coinsSidebar} alt="" />
                  Critérios Financeiros
                </h4>
                <div>
                  <strong>Valor teto:</strong>
                  {' '}
                  {consultData?.valorTeto ? `${floatToCurrency(consultData.valorTeto)}` : 'Não utiliza'}
                </div>

                {routingType === 2 && (
                  <div>
                    <strong>Valor inicial:</strong>
                    {' '}
                    {consultData?.valorVT ? `${floatToCurrency(consultData.valorVT)}` : 'Não informado'}
                  </div>
                )}

                <div>
                  <strong>SPTRANS (Bilhete Comum ou VT)</strong>
                  {' '}
                  {consultData?.semTaxaVT ?  'Bilhete Comum' : 'VT'}
                </div>

                <div>
                  <strong>AESA (Bilhete Comum ou VT)</strong>
                  {' '}
                  {consultData?.regraVTSemTxAesa ?  'Bilhete Comum' : 'VT'}
                </div>

                <div>
                  <strong>Integrações:</strong>
                  {' '}
                  {consultData?.semIntegraVT ?  'Não integra' : 'Realiza integrações'}
                </div>
              </InnerDataContainer>

              <InnerDataContainer>
                <h4>
                  <img src={user} alt="" />
                  Critérios Colaborador
                </h4>

                <div>
                  <strong>Horário Entrada:</strong>
                  {' '}
                  {consultData.horaEntra}
                </div>

                <div>
                  <strong>Horário Saída:</strong>
                  {' '}
                  {consultData.horaSai}
                </div>

                <div>
                  <strong>Dias trabalhados:</strong>
                  {' '}
                  {consultData?.diasVT}
                </div>

                <div>
                  <strong>Salário:</strong>
                  {' '}
                  {consultData?.salario > 3200 ?  'Acima de 3205,20' : 'Abaixo de 3205,20'}
                </div>
              </InnerDataContainer>
            </SelectedViewContainer>

          </OpacityAnimation>
        )}

        {selectedCard === 'particularities' && (
          <OpacityAnimation>
            <SelectedViewContainer>
              <InnerDataContainer>
                <h4>
                  <img src={companySidebar} alt="" />
                  Particularidades (observações) da empresa
                </h4>
                <div>
                  {particularities}
                </div>
              </InnerDataContainer>
            </SelectedViewContainer>

          </OpacityAnimation>
        )}
      </DataContainer>

      <button onClick={() => selectedCard === 'data' ? setEditConsultDataModalShow(true) : setEditSettingsDataModalShow(true)} disabled={!canEdit && selectedCard !== 'data'}>
        <img src={edit} alt="edit" title="Editar dados" className="primaryColor" />
      </button>
    </Container>
  );
}
